"use client";

import * as React from "react";
import { useNavatticDemo } from "@repo/utils/hooks/useNavatticDemo";

export type NavatticDemoButtonProps = Omit<
  React.ComponentProps<"button">,
  "type"
> & {
  /** Navattic popup embed URL, e.g. `https://capture.navattic.com/<demoId>`. */
  url: string;
  /** Title shown in the lightbox header. Defaults to Navattic's own behaviour. */
  title?: string;
  /**
   * Separate mobile flow of the demo, opened on phones (see
   * `NAVATTIC_MOBILE_MEDIA_QUERY`) with the same `title`. Falls back to `url`
   * when missing or invalid.
   */
  mobileUrl?: string;
  /** iframe `allow` attribute. Defaults to `"fullscreen; autoplay"`. */
  allow?: string;
  /** Lock page scroll while the demo is open. Default `true`. */
  lockScroll?: boolean;
  onOpen?: () => void;
  /** Fires when the visitor closes the demo (close button, backdrop, Escape). */
  onDemoClose?: () => void;
};

/**
 * Client-side trigger behind `Button`'s `navatticUrl` prop.
 *
 * It exists so that the Storyblok button resolver can stay serializable (plain
 * strings) while running inside Server Components: the click handler is
 * created here, on the client, never in the resolver. Any `onClick` passed in
 * (section-level analytics) runs first; the demo opens unless it called
 * `preventDefault()`. Mirrors `@repo/ui/NavatticDemoButton` on arrive.
 *
 * Renders nothing when `url` is not an allowed Navattic URL.
 */
export const NavatticDemoButton = React.forwardRef<
  HTMLButtonElement,
  NavatticDemoButtonProps
>(function NavatticDemoButton(
  {
    url,
    title,
    mobileUrl,
    allow,
    lockScroll,
    onOpen,
    onDemoClose,
    onClick,
    onPointerEnter,
    onFocus,
    children,
    ...rest
  },
  ref,
) {
  const { isValid, isOpen, isLoading, open, preload } = useNavatticDemo({
    url,
    title,
    mobileUrl,
    allow,
    lockScroll,
    onOpen,
    onClose: onDemoClose,
  });

  if (!isValid) return null;

  return (
    <button
      ref={ref}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-busy={isLoading || undefined}
      {...rest}
      // After the spread on purpose: a demo trigger must never submit a form,
      // even when a consumer forwards type="submit" (ContactSection does).
      type="button"
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) void open(event.currentTarget);
      }}
      onPointerEnter={(event) => {
        onPointerEnter?.(event);
        preload();
      }}
      onFocus={(event) => {
        onFocus?.(event);
        preload();
      }}
    >
      {children}
    </button>
  );
});
